/**
 * Farben der Fahrzeuge.
 *
 * Ausgewählt für Ablesbarkeit bei Sonne auf einer hellen Karte: durchweg
 * kräftige, dunkle Töne, keine Pastellfarben. Die Schreibweise ist klein und
 * sechsstellig — genau so, wie es die Prüfregel der Spalte participant.color
 * verlangt.
 *
 * ## Zwei Arten von Farbe
 *
 * **Rollenfarben** gehören der Rolle, nicht dem Fahrzeug: Die Führung ist immer
 * orange, der Schlusswagen immer rot. Sie werden nirgends gespeichert, sondern
 * beim Anzeigen aus der Rolle bestimmt — siehe `displayColor`. Bei einer
 * Übergabe wandert die Farbe damit im selben Augenblick mit, in dem die
 * Rollenänderung eintrifft, ohne zusätzliche Nachricht und ohne Schreibvorgang
 * mitten in der Fahrt. Wer die Führung abgibt, fällt auf seine persönliche
 * Farbe zurück, die er die ganze Zeit behalten hat.
 *
 * **Persönliche Farben** gehören dem Fahrzeug und werden beim Beitritt einmal
 * vergeben. Die beiden Rollenfarben sind darin nicht enthalten — sonst sähe ein
 * gewöhnliches Fahrzeug aus wie die Spitze oder das Ende der Kolonne.
 *
 * Farbe allein kennzeichnet nie eine Rolle oder einen Zustand. Auf der Karte
 * trägt jedes Fahrzeug zusätzlich sein Namenskürzel, Führung und Schlusswagen
 * haben eine eigene Form, und Zustände werden über Form und Beschriftung
 * unterschieden.
 */
import { z } from 'zod';
/**
 * Dem Führungsfahrzeug vorbehalten. Dieselbe Farbe trägt die Führungsspur —
 * Spitze und Spur erklären sich damit gegenseitig.
 *
 * Wird sie geändert, gehört das Gegenstück `--führung` in
 * `apps/web/src/styles.css` mit geändert; dort steht sie für Umrandungen, die
 * im dunklen Modus einen helleren Ton brauchen.
 */
export const LEADER_COLOR = Object.freeze({ hex: '#ff6f00', name: 'Orange' });
/**
 * Dem Schlusswagen vorbehalten. Rot wie das Schlusslicht: ein Bild, das jeder
 * Fahrer ohne Erklärung versteht.
 */
export const SWEEPER_COLOR = Object.freeze({ hex: '#e6194b', name: 'Rot' });
/**
 * Die Navigation auf der Karte — Stufe 2.
 *
 * Bewusst weit weg von Orange und Rot: Auf dem Bildschirm liegen Führungsspur
 * und Rückweg über weite Strecken nebeneinander, und zwei ähnliche Töne wären
 * bei Sonne nicht zu trennen. Kein Fahrzeug trägt diesen Ton — er ist keine
 * persönliche Farbe (siehe VEHICLE_COLORS) und steht damit für genau eine
 * Sache.
 */
export const ROUTE_COLOR = Object.freeze({ hex: '#0aa5ff', name: 'Leuchtblau' });
/**
 * Die **geplante** Route auf der Karte — Stufe 3.
 *
 * Drei Linien liegen jetzt übereinander, und sie sagen Verschiedenes: Die
 * orange Führungsspur ist, **wo die Kolonne wirklich gefahren ist**; die blaue
 * Navigation ist der Weg zurück; diese hier ist, was jemand sich vorgenommen
 * hat. Der Plan ist die schwächste Aussage von dreien und tritt deshalb zurück
 * — hinter den anderen beiden gezeichnet, dünner, gestrichelt.
 *
 * **Gestrichelt ist nicht Zierrat.** Abschnitt 2 des Leitfadens verbietet,
 * einen Zustand allein über die Farbe zu tragen; bei Sonne auf dem Display ist
 * der Unterschied zwischen zwei Farbtönen das Erste, was verlorengeht. Die
 * Strichelung trägt die Unterscheidung auch dann noch.
 *
 * Violett, weil Orange, Rot und Blau vergeben sind und der Ton auf einer
 * grauen Karte auch dann sichtbar bleibt, wenn er dünn gezeichnet ist.
 */
export const PLANNED_ROUTE_COLOR = Object.freeze({
    hex: '#8e44ad',
    name: 'Violett',
});
/**
 * Anfang und Ende der geplanten Strecke.
 *
 * Sie stehen hier und nicht in der Oberfläche, weil es sie **zweimal** gibt:
 * im Umriss nach dem Einlesen und auf der Karte der Planung. Zwei Töne für
 * denselben Punkt wären eine Falschauskunft — und beim nächsten Ändern hätte
 * einer von beiden ihn.
 *
 * Niemals allein über die Farbe: Beide tragen auf der Karte ihre Beschriftung,
 * und der Umriss steht ohnehin neben dem Satz, der sie erklärt.
 */
export const ROUTE_START_COLOR = Object.freeze({
    hex: '#2e7d32',
    name: 'Grün',
});
export const ROUTE_END_COLOR = Object.freeze({
    hex: '#c62828',
    name: 'Rot',
});
/** Erste persönliche Farbe. Benannt, damit die Vergabe unten ohne Zweifelsfall auskommt. */
const BLAU = Object.freeze({ hex: '#4363d8', name: 'Blau' });
/** Auswählbare Farben für die übrigen Fahrzeuge, in der Reihenfolge der Vergabe. */
export const VEHICLE_COLORS = Object.freeze([
    BLAU,
    { hex: '#3cb44b', name: 'Grün' },
    { hex: '#911eb4', name: 'Violett' },
    { hex: '#008080', name: 'Petrol' },
    { hex: '#9a6324', name: 'Braun' },
    { hex: '#800000', name: 'Weinrot' },
    { hex: '#808000', name: 'Oliv' },
    { hex: '#000075', name: 'Marineblau' },
    { hex: '#f032e6', name: 'Magenta' },
    { hex: '#469990', name: 'Türkis' },
    { hex: '#2f4f4f', name: 'Schiefergrau' },
]);
/** Ist diese Farbe einer Rolle vorbehalten? */
export function isReservedColor(hex) {
    const wert = hex.toLowerCase();
    return wert === LEADER_COLOR.hex || wert === SWEEPER_COLOR.hex;
}
/**
 * Eine persönliche Farbe, wie sie über die Schnittstelle hereinkommen darf.
 *
 * Die beiden Rollenfarben sind ausgeschlossen: Wer sie sich setzen könnte, sähe
 * aus wie die Spitze oder das Ende der Kolonne, ohne es zu sein.
 */
export const personalColorSchema = z
    .string()
    .regex(/^#[0-9a-f]{6}$/)
    .refine((hex) => !isReservedColor(hex), {
    message: 'Diese Farbe ist der Führung und dem Schlusswagen vorbehalten.',
});
/**
 * Nächste noch freie Farbe. Sind alle vergeben — bei mehr Fahrzeugen als
 * Farben —, beginnt die Vergabe von vorn; die Namenskürzel auf der Karte
 * unterscheiden die Fahrzeuge dann weiterhin.
 */
export function nextFreeColor(used, index = 0) {
    const taken = new Set(used.map((hex) => hex.toLowerCase()));
    const free = VEHICLE_COLORS.find((color) => !taken.has(color.hex));
    if (free !== undefined) {
        return free;
    }
    return VEHICLE_COLORS[index % VEHICLE_COLORS.length] ?? BLAU;
}
/**
 * Die Farbe, in der ein Fahrzeug dargestellt wird: die seiner Rolle, sonst
 * seine eigene.
 *
 * Diese Funktion ist die einzige Stelle, an der über die Anzeigefarbe
 * entschieden wird — Kolonnenliste, Karte und ein späterer Flutter-Client
 * benutzen dieselbe.
 */
export function displayColor(role, personalColor) {
    switch (role) {
        case 'leader':
            return LEADER_COLOR.hex;
        case 'sweeper':
            return SWEEPER_COLOR.hex;
        case 'participant':
            return personalColor;
    }
}
//# sourceMappingURL=colors.js.map